import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../store';
import { Router } from '@angular/router';
import { ProcessService, ProcessType } from '../../services/process.service';
import * as fromProcess from '../../store/process/process.reducer';
import { ApiState } from '../../model/api-state.model';
import { combineLatest, filter, first, Subject, takeUntil } from 'rxjs';
import { PurchaseAgentPayload } from '../../model/purchase-agent.model';
import { FormsService } from 'src/app/services/forms.service';
import { PathPayload } from 'src/app/model/path.model';

const MIN_SPLITS_REQUIRED = 1;

@Component({
  selector: 'app-conclusion-request',
  templateUrl: './conclusion-request.component.html',
})
export class ConclusionRequestComponent implements OnInit, OnDestroy {
  conclusionStatus$ = this.store.select(fromProcess.selectProcessConclusionState);
  conclusionError$ = this.store.select(fromProcess.selectProcessConclusionError);
  formOfPayment$ = this.store.select(fromProcess.selectProcessFormOfPayment);
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  selectedPackage$ = this.store.select(fromProcess.selectProcessPackageName);
  addons$ = this.store.select(fromProcess.selectProcessAddons);
  premiumCalculation$ = this.store.select(fromProcess.selectPremiumCalculation);
  sendStaticAttachmentsState$ = this.store.select(fromProcess.selectSendStaticAttachmentsState);
  sendBackOfficeData$ = this.store.select(fromProcess.selectSendBackOfficeData);
  processHashCode$ = this.store.select(fromProcess.selectProcessHashCode);

  ngDestroy$ = new Subject<void>();

  apiState = ApiState;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private processService: ProcessService,
    private formsService: FormsService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromProcess.actions.setStep({ step: 8 }));
    this.processService.onPageInit();

    this.implementConclusionSuccess();
    this.makeRequest();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  makeRequest() {
    combineLatest([
      this.premiumCalculation$,
      this.formOfPayment$,
      this.addons$,
      this.selectedPackage$,
      this.sendStaticAttachmentsState$,
      this.sendBackOfficeData$,
      this.processHashCode$,
    ])
      .pipe(first())
      .subscribe(
        ([
          { processKind, discountCode, selectedAgentSection },
          formOfPayment,
          addons,
          selectedPackage,
          sendStaticAttachmentsState,
          sendBackOfficeData,
          hashCode,
        ]) => {
          let path: PathPayload;

          switch (processKind) {
            case ProcessType.TRIPABROAD:
              path = {
                tripAbroad: this.formsService.tripAbroadApi,
              };
              break;
            case ProcessType.TRAVELINPL:
              path = {
                travelInPL: this.formsService.travelInPlApi,
              };
              break;
            case ProcessType.RESIGNATION:
              path = {
                resignation: this.formsService.resignationApi,
              };
              break;
            default:
              throw new Error('Unknown process kind');
          }

          const agreements: number[] = [];

          const marketing = this.formsService.agreementsForm.value.marketing as Record<string, boolean>;
          const statements = this.formsService.agreementsForm.value.statements as Record<string, boolean>;

          Object.entries(marketing)
            .filter(([_, value]) => value)
            .forEach(([key]) => {
              const agreementsId = Number(key.replace('agreement.', ''));

              agreements.push(agreementsId);
            });

          Object.entries(statements)
            .filter(([_, value]) => value)
            .forEach(([key]) => {
              const agreementsId = Number(key.replace('agreement.', ''));

              agreements.push(agreementsId);
            });

          const payload: PurchaseAgentPayload = {
            path,
            policyholder: this.formsService.policyholderApi,
            insureds: this.formsService.insuredsApi,
            backofficeSection: sendBackOfficeData ? this.formsService.backOfficeSectionApi : null,
            agentCommissionSplits:
              this.formsService.agentCommissionSplits.controls.splits.length > MIN_SPLITS_REQUIRED
                ? this.formsService.agentCommisionSplitsApi
                : null,
            selectedOffert: {
              packageCodeName: selectedPackage,
              addOns: addons ?? {},
            },
            ...this.processService.apiPayload,
            discountCode: {
              code: discountCode || '',
            },
            ...(!(selectedAgentSection.premiumPercentIncrease === undefined || selectedAgentSection.premiumPercentIncrease === null)
              ? {
                  agentSection: selectedAgentSection,
                }
              : {}),
            ...(formOfPayment ? { formOfPayment } : {}),
            agreements: this.formsService.agreementsApi,
            emailWithDocumentsWasSent: sendStaticAttachmentsState === ApiState.success,
            calculationHashCode: hashCode,
          };

          this.store.dispatch(fromProcess.actions.conclusionStart({ payload }));
        },
      );
  }

  private implementConclusionSuccess() {
    this.conclusionStatus$
      .pipe(
        takeUntil(this.ngDestroy$),
        filter((state) => state === ApiState.success),
      )
      .subscribe(() => {
        this.store.dispatch(fromProcess.actions.setSuccess({ success: true }));

        void this.router.navigate(['/page/success']);
      });
  }
}
